import { Feature } from 'ol';
import type { Geometry } from 'ol/geom';
import { Circle, Point } from 'ol/geom';
import { Modify } from 'ol/interaction';
import { Vector as VectorLayer } from 'ol/layer';
import { Vector as VectorSource } from 'ol/source';
import type View from 'ol/View';

import type { GeoJsonStylePropertiesDTO } from '@nora-monorepo/nora-api-client';
import { DrawToolType } from '@nora-monorepo/nora-api-client';
import type { BBox } from '@nora-monorepo/shared';

import {
  CreateFeatureBaseDrawTool,
} from '#nora-ui/map/drawtools/create-feature-base-draw-tool';
import { getHandleModifyPixelTolerance } from '#nora-ui/map/drawtools/draw-tool-utils';
import { getHandleStyle } from '#nora-ui/map/drawtools/styles/circle-style';
import { getFeatureStyle } from '#nora-ui/map/drawtools/styles/styles';
import type { DrawToolOptions } from '#nora-ui/map/drawtools/types';
import { extractDrawToolOptions } from '#nora-ui/map/drawtools/types';
import type { DrawToolManager } from '#nora-ui/map/map/DrawToolManager';

type HandleType = 'center-handle' | 'radius-handle';
type CircleFeatureProperties = Partial<GeoJsonStylePropertiesDTO>;

/**
 * The CircleTool manages a single circle feature with:
 * - A center handle moves the circle
 * - A radius handle changes the circle radius
 * - It saves as a Point with a radius
 */
export class CircleTool extends CreateFeatureBaseDrawTool {
  drawToolType: DrawToolType = DrawToolType.circle;
  options: DrawToolOptions = {
    drawToolType: this.drawToolType,
    radiusLineVisible: true,
  };

  private readonly modifyInteraction: Modify;
  private readonly handleSource: VectorSource;
  private readonly handleLayer: VectorLayer<VectorSource>;

  private centerHandle!: Feature<Point>;
  private radiusHandle!: Feature<Point>;
  private circleFeature!: Feature<Circle>;
  private isInitialized = false;
  private radiusLineVisible = true;

  // the handle currently being modified
  private currentHandleType: HandleType | null = null;

  constructor(drawToolManager: DrawToolManager) {
    super(drawToolManager);
    this.drawToolName = 'Circle';
    this.radiusLineVisible = drawToolManager.drawStyleOptions.radiusLineVisible ?? true;
    this.options.radiusLineVisible = this.radiusLineVisible;

    this.handleSource = new VectorSource();
    this.handleLayer = new VectorLayer<VectorSource>({
      source: this.handleSource,
      style: getHandleStyle,
      zIndex: 10000,
    });

    this.modifyInteraction = new Modify({
      source: this.handleSource,
      style: getHandleStyle,
      insertVertexCondition: () => false,
      pixelTolerance: getHandleModifyPixelTolerance(),
    });

    this.modifyInteraction.on('modifystart', (event) => {
      this.handleLayer.setVisible(false);
      const features = event.features.getArray();
      const handleFeature = features.at(0);
      if (!handleFeature) {
        return;
      }

      const handleType = handleFeature.get('type') as string | undefined;

      if (handleType !== 'center-handle' && handleType !== 'radius-handle') {
        return;
      }
      this.currentHandleType = handleType;
    });

    this.modifyInteraction.on('modifyend', () => {
      this.handleLayer.setVisible(true);
      this.updateHandles();
      this.currentHandleType = null;
    });
  }

  override getDefaultProperties(): CircleFeatureProperties {
    return {
      ...super.getDefaultProperties(),
      radiusLineVisible: this.radiusLineVisible,
    };
  }

  override activate(): void {
    super.activate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.addLayer(this.handleLayer);
    map.addInteraction(this.modifyInteraction);
    if (!this.isInitialized) {
      this.createInitialCircle();
      this.isInitialized = true;
    }
    this.addHandles(this.circleFeature);
    this.handleLayer.setVisible(true);
  }

  override deactivate(): void {
    super.deactivate();
    const map = this.drawToolManager.map.getOlMapOrThrow();
    map.removeInteraction(this.modifyInteraction);
    this.handleSource.clear();
    this.handleLayer.setVisible(false);
    map.removeLayer(this.handleLayer);
  }

  private createInitialCircle(): void {
    const map = this.drawToolManager.map.getOlMapOrThrow();

    const view: View = map.getView();
    const center: [number, number] = (view.getCenter() as [number, number] | undefined) ?? [
      0,
      0,
    ];

    const size = map.getSize();
    if (!size) {
      return;
    }

    const [width, height] = size;
    if (width === undefined || height === undefined) {
      return;
    }
    const resolution = view.getResolution() ?? 1;
    const initialSize = (Math.min(width, height) / 4) * resolution;

    const circleGeometry = new Circle(center, initialSize);
    this.circleFeature = new Feature<Circle>(circleGeometry);
    this.circleFeature.setProperties(this.getDefaultProperties());
    this.circleFeature.setStyle(getFeatureStyle);

    this.editingVectorSource.clear();
    this.editingVectorSource.addFeature(this.circleFeature);
  }

  private addHandles(circleFeature: Feature<Circle>): void {
    const geometry = circleFeature.getGeometry() as Circle;
    const center = geometry.getCenter();
    const radius = geometry.getRadius();
    const [centerX, centerY] = center;
    if (centerX === undefined || centerY === undefined) {
      return;
    }
    const radiusHandlePoint: [number, number] = [centerX + radius, centerY];

    this.centerHandle = new Feature<Point>(new Point(center));
    this.centerHandle.set('type', 'center-handle');

    this.radiusHandle = new Feature<Point>(new Point(radiusHandlePoint));
    this.radiusHandle.set('type', 'radius-handle');

    this.handleSource.clear();
    this.handleSource.addFeature(this.centerHandle);
    this.handleSource.addFeature(this.radiusHandle);

    const centerGeometry = this.centerHandle.getGeometry() as Point;
    const radiusGeometry = this.radiusHandle.getGeometry() as Point;

    centerGeometry.on('change', () => {
      if (this.currentHandleType === 'center-handle') {
        this.updateCircleTransform();
      }
    });

    radiusGeometry.on('change', () => {
      if (this.currentHandleType === 'radius-handle') {
        this.updateCircleScale();
      }
    });

    this.handleLayer.setVisible(true);
  }

  private updateCircleTransform(): void {
    const circleGeometry = this.circleFeature.getGeometry() as Circle;
    const centerGeometry = this.centerHandle.getGeometry() as Point;
    const newCenter = centerGeometry.getCoordinates();
    circleGeometry.setCenter(newCenter);
    this.circleFeature.changed();
  }

  private updateCircleScale(): void {
    const circleGeometry = this.circleFeature.getGeometry() as Circle;
    const center = (this.centerHandle.getGeometry() as Point).getCoordinates();
    const radiusCoordinate = (
      this.radiusHandle.getGeometry() as Point
    ).getCoordinates();
    const [centerX, centerY] = center;
    const [radiusX] = radiusCoordinate;
    if (centerX === undefined || centerY === undefined || radiusX === undefined) {
      return;
    }
    const dx = radiusX - centerX;
    const newRadius = Math.abs(dx);
    circleGeometry.setRadius(newRadius);
    this.circleFeature.changed();
  }

  private updateHandles(): void {
    const circleGeometry = this.circleFeature.getGeometry() as Circle;
    const center = (this.centerHandle.getGeometry() as Point).getCoordinates();
    const radius = circleGeometry.getRadius();
    const [centerX, centerY] = center;
    if (centerX === undefined || centerY === undefined) {
      return;
    }
    (this.radiusHandle.getGeometry() as Point).setCoordinates([
      centerX + radius,
      centerY,
    ]);
    this.radiusHandle.changed();
    this.markDirty();
  }

  override async save(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    const circleGeometry = this.circleFeature.getGeometry() as Circle;
    const center = circleGeometry.getCenter();
    const radius = circleGeometry.getRadius();

    const pointFeature = new Feature<Point>(new Point(center));
    pointFeature.setProperties({
      ...this.getDefaultProperties(),
      id: this.featureId,
      radius,
    });

    this.feature = pointFeature;

    await super.save();
  }

  override onStylingUpdated(): void {
    super.onStylingUpdated();
    this.circleFeature.setProperties(this.getDefaultProperties());
    this.circleFeature.setStyle(getFeatureStyle);
    this.markDirty();
  }

  isRadiusLineVisible(): boolean {
    return this.radiusLineVisible;
  }

  setRadiusLineVisible(visible: boolean): void {
    if (this.radiusLineVisible === visible) {
      return;
    }

    this.radiusLineVisible = visible;
    this.options.radiusLineVisible = visible;
    this.drawToolManager.updateDrawStyleOptions({
      radiusLineVisible: visible,
    });
    this.markDirty();
  }

  override showFillColor(): boolean {
    return true;
  }

  override loadFeature(feature: Feature<Geometry>): boolean {
    const geometry = feature.getGeometry();
    if (!(geometry instanceof Point)) {
      return false;
    }

    this.featureId = feature.get('id') as string | undefined;

    const center = geometry.getCoordinates() as [number, number];
    const radius = feature.get('radius') as number | undefined;

    if (typeof radius !== 'number' || radius <= 0) {
      return false;
    }

    // convert point feature to a circle
    const circleGeometry = new Circle(center, radius);
    const featureProperties = extractDrawToolOptions(feature);
    this.radiusLineVisible = featureProperties.radiusLineVisible ?? true;
    this.options.radiusLineVisible = this.radiusLineVisible;
    const resolvedFeatureProperties = {
      ...featureProperties,
      radiusLineVisible: this.radiusLineVisible,
    };
    this.drawToolManager.updateDrawStyleOptions(resolvedFeatureProperties);

    const circleFeature = new Feature<Circle>(circleGeometry);
    this.circleFeature = circleFeature;
    this.circleFeature.setProperties({
      ...this.getDefaultProperties(),
      ...resolvedFeatureProperties,
      radius,
      id: this.featureId,
    });
    this.circleFeature.setStyle(getFeatureStyle);
    this.editingVectorSource.clear();
    this.isInitialized = true;
    super.loadFeature(circleFeature);
    return true;
  }

  override getFeatureBoundingBox(): BBox | null {
    return super.getFeatureBoundingBox(this.circleFeature);
  }
}
